import { useEffect, useState } from 'react';

// How long the item selection must rest before anything asks the server
// about the selected item. Holding ↓ moves it at key-repeat speed (~30 ms a
// step); this is long enough to let that pass and short enough not to be
// felt on a click.
export const SELECTION_SETTLE_MS = 150;

/**
 * `value`, once it has held still for `delayMs` — a trailing debounce of a
 * value rather than of a function. The first value is settled at once, so a
 * surface that mounts on a choice already made (a deep link, the pane
 * opening) waits for nothing; only a CHANGE waits. A value that moves away
 * and back before the delay is out never settles on the one in between.
 *
 * Callers compare: `useSettledValue(id, ms) === id` is "the selection has
 * rested on this id", and is what the fetches that describe it wait for.
 */
export default function useSettledValue(value, delayMs) {
	const [settled, setSettled] = useState(value);
	useEffect(() => {
		if (Object.is(settled, value)) return undefined;
		const timer = setTimeout(() => setSettled(value), delayMs);
		return () => clearTimeout(timer);
	}, [value, settled, delayMs]);
	return settled;
}
